class KlaviyoForm extends HTMLElement {
  constructor() {
    super()
    this.apiUrl = 'https://a.klaviyo.com/client/subscriptions?company_id=SSMGf5'
    this.listId = this.getAttribute('list-id')
  }

  connectedCallback() {
    const form = this.querySelector('form')
    const inputs = form.querySelectorAll('input, select, textarea')
    const submitButton = form.querySelector('[type="submit"]')

    for (const input of inputs) {
      input.addEventListener('focus', () => {
        const stateMessages = form.querySelectorAll('.newsletter-form__message')
        for (const message of stateMessages) {
          message.remove()
        }
        // Remove accessibility attributes from the focused input
        input.removeAttribute('autofocus')
        input.removeAttribute('aria-invalid')
        input.removeAttribute('aria-describedby')
      })
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      submitButton.setAttribute('disabled', true)

      try {
        const formData = new FormData(form)
        const data = Object.fromEntries(formData.entries())
        await this.submitForm(data)
        this.handleSuccess(form)
      } catch (err) {
        this.handleError(form, err)
      } finally {
        submitButton.removeAttribute('disabled')
      }
    })
  }

  creatProfileAttributes(data) {
    // Separate out standard fields and those that belong to `properties`
    const properties = {}
    const profileAttributes = {}

    // Iterate over all the form fields
    for (const [key, value] of Object.entries(data)) {
      // property fields are named something like 'properties[xyz]
      const isPropertiesField = key.match(/^properties\[(.+)\]$/)

      if (isPropertiesField) {
        const propertyKey = isPropertiesField[1]
        properties[propertyKey] = value
      } else {
        const tk =
          key === 'phone_number' ? this.formatPhoneNumber(value) : value
        profileAttributes[key] = tk
      }
    }

    return {
      ...profileAttributes,
      properties,
    }
  }

  formatPhoneNumber(phoneNumber) {
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '')

    // Check if the cleaned number starts with the country code, e.g., 1 for the US
    if (cleaned.length === 10) {
      // Assume default country code if not provided, e.g., "1" for US
      return `+1${cleaned}`
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // If the number includes the country code already
      return `+${cleaned}`
    }
    if (cleaned.length > 11) {
      // If it's an international number with a valid format
      return `+${cleaned}`
    }

    throw new Error('Invalid phone number format')
  }

  async submitForm(data) {
    const attributes = this.creatProfileAttributes(data)

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: { revision: '2024-07-15', 'content-type': 'application/json' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes } },
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } },
        },
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw errorData
    }
    return response
  }

  handleSuccess(form) {
    form.reset()

    const event = new CustomEvent('klaviyo-form:success', {
      bubbles: true,
      cancelable: true,
    })
    if (!this.dispatchEvent(event)) return

    const successHTML = `
      <small class="form__message--success form__message" tabindex="-1" autofocus>
        Success!
      </small>
    `
    form.insertAdjacentHTML('afterend', successHTML)
  }

  handleError(form, err) {
    let errorMessages = []

    if (err.errors?.length) {
      // Map through errors to create an array of error messages
      errorMessages = err.errors.map(
        (error) => `
        <small class="form__message--error form__message">
          ${error.detail || 'An error occurred'}
        </small>
      `,
      )
    } else {
      // Handle case where err.errors is not available or empty
      errorMessages.push(`
        <small class="form__message--error form__message">
          ${err.message || 'An error occurred'}
        </small>
      `)
    }

    const errorHTML = errorMessages.join('')

    form.insertAdjacentHTML('afterend', errorHTML)
  }
}

customElements.define('klaviyo-form', KlaviyoForm)
